import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  useDLQMessages,
  useDLQQueues,
  useDeleteMessage,
  usePurgeQueue,
  useRedriveMessages,
} from '@/hooks/use-dlq'
import type { DLQMessage, DLQQueueInfo } from '@/services/dlq.service'
import { AlertTriangle, ArrowLeft, ChevronRight, RefreshCw, Trash2 } from 'lucide-react'
import { useState } from 'react'

// Queue name to friendly display name
const QUEUE_DISPLAY_NAMES: Record<string, string> = {
  'pelgo-content-generator-dlq-prod': 'Content Generator',
  'pelgo-document-render-dlq-prod': 'Document Renderer',
  'pelgo-job-matcher-dlq-prod': 'Job Matcher',
  'pelgo-sms-messages-dlq-prod': 'SMS Messages',
}

function QueueCard({
  queue,
  onSelect,
}: {
  queue: DLQQueueInfo
  onSelect: (queue: DLQQueueInfo) => void
}) {
  const displayName = QUEUE_DISPLAY_NAMES[queue.name] || queue.name
  const hasMessages = queue.message_count > 0

  return (
    <Card
      className={`cursor-pointer hover:shadow-md transition-shadow ${hasMessages ? 'border-amber-200' : ''}`}
      onClick={() => onSelect(queue)}
    >
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{displayName}</CardTitle>
        {hasMessages && <AlertTriangle className="h-4 w-4 text-amber-500" />}
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between">
          <div>
            <div className="text-2xl font-bold">{queue.message_count}</div>
            <p className="text-xs text-muted-foreground">
              {queue.message_count === 1 ? 'message' : 'messages'}
            </p>
          </div>
          <ChevronRight className="h-5 w-5 text-muted-foreground" />
        </div>
      </CardContent>
    </Card>
  )
}

function MessageCard({
  message,
  onDelete,
  onRedrive,
  isDeleting,
  isRedriving,
}: {
  message: DLQMessage
  onDelete: () => void
  onRedrive: () => void
  isDeleting: boolean
  isRedriving: boolean
}) {
  const [expanded, setExpanded] = useState(false)

  // Format the timestamp
  const timestamp = message.sent_timestamp
    ? new Date(message.sent_timestamp).toLocaleString()
    : 'Unknown'

  // Try to pretty-print the body
  let bodyDisplay: string
  try {
    if (typeof message.body === 'object') {
      bodyDisplay = JSON.stringify(message.body, null, 2)
    } else {
      bodyDisplay = message.body_raw
    }
  } catch {
    bodyDisplay = message.body_raw
  }

  return (
    <Card className="mb-4">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <CardTitle className="text-sm font-mono">{message.message_id}</CardTitle>
            <CardDescription>
              Sent: {timestamp} | Receive count: {message.approximate_receive_count}
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onRedrive} disabled={isRedriving}>
              <RefreshCw className={`h-4 w-4 mr-1 ${isRedriving ? 'animate-spin' : ''}`} />
              Redrive
            </Button>
            <Button variant="outline" size="sm" onClick={onDelete} disabled={isDeleting}>
              <Trash2 className="h-4 w-4 mr-1 text-destructive" />
              Delete
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Message Attributes */}
        {Object.keys(message.message_attributes).length > 0 && (
          <div className="mb-4">
            <h4 className="text-sm font-semibold mb-2">Attributes</h4>
            <div className="bg-muted rounded-md p-3">
              {Object.entries(message.message_attributes).map(([key, attr]) => (
                <div key={key} className="text-sm">
                  <span className="font-medium">{key}:</span> {attr.value}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Message Body */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-semibold">Message Body</h4>
            <Button variant="ghost" size="sm" onClick={() => setExpanded(!expanded)}>
              {expanded ? 'Collapse' : 'Expand'}
            </Button>
          </div>
          <pre
            className={`bg-muted rounded-md p-3 text-xs overflow-auto ${expanded ? '' : 'max-h-32'}`}
          >
            {bodyDisplay}
          </pre>
        </div>
      </CardContent>
    </Card>
  )
}

function QueueDetail({
  queue,
  onBack,
}: {
  queue: DLQQueueInfo
  onBack: () => void
}) {
  const displayName = QUEUE_DISPLAY_NAMES[queue.name] || queue.name
  const { data, isLoading, refetch } = useDLQMessages(queue.name)
  const redriveMessages = useRedriveMessages()
  const deleteMessage = useDeleteMessage()
  const purgeQueue = usePurgeQueue()
  const [showPurgeDialog, setShowPurgeDialog] = useState(false)
  const [showRedriveAllDialog, setShowRedriveAllDialog] = useState(false)

  const handleRedriveAll = async () => {
    await redriveMessages.mutateAsync({ queueName: queue.name })
    setShowRedriveAllDialog(false)
  }

  const handlePurge = async () => {
    await purgeQueue.mutateAsync(queue.name)
    setShowPurgeDialog(false)
  }

  const handleRedriveSingle = async (messageId: string) => {
    await redriveMessages.mutateAsync({ queueName: queue.name, messageIds: [messageId] })
  }

  const handleDeleteSingle = async (receiptHandle: string) => {
    await deleteMessage.mutateAsync({ queueName: queue.name, receiptHandle })
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          <div>
            <h2 className="text-xl font-bold">{displayName}</h2>
            <p className="text-sm text-muted-foreground">{queue.name}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => refetch()} disabled={isLoading}>
            <RefreshCw className={`h-4 w-4 mr-1 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          {queue.message_count > 0 && (
            <>
              <Button variant="outline" onClick={() => setShowRedriveAllDialog(true)}>
                <RefreshCw className="h-4 w-4 mr-1" />
                Redrive All
              </Button>
              <Button variant="destructive" onClick={() => setShowPurgeDialog(true)}>
                <Trash2 className="h-4 w-4 mr-1" />
                Purge Queue
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Source Queue Info */}
      {queue.source_queue_name && (
        <div className="text-sm text-muted-foreground">
          Source queue: <span className="font-mono">{queue.source_queue_name}</span>
        </div>
      )}

      {/* Messages */}
      {isLoading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-40 bg-gray-100 rounded animate-pulse" />
          ))}
        </div>
      ) : data?.messages && data.messages.length > 0 ? (
        <div>
          <p className="text-sm text-muted-foreground mb-4">
            Showing {data.count} of {queue.message_count} messages
          </p>
          {data.messages.map((message) => (
            <MessageCard
              key={message.message_id}
              message={message}
              onDelete={() => handleDeleteSingle(message.receipt_handle)}
              onRedrive={() => handleRedriveSingle(message.message_id)}
              isDeleting={deleteMessage.isPending}
              isRedriving={redriveMessages.isPending}
            />
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            No messages in this queue
          </CardContent>
        </Card>
      )}

      {/* Purge Confirmation Dialog */}
      <Dialog open={showPurgeDialog} onOpenChange={setShowPurgeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Purge Queue</DialogTitle>
            <DialogDescription>
              Are you sure you want to purge all messages from {displayName}? This will permanently
              delete approximately {queue.message_count} messages. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPurgeDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handlePurge} disabled={purgeQueue.isPending}>
              {purgeQueue.isPending ? 'Purging...' : 'Purge All Messages'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Redrive All Confirmation Dialog */}
      <Dialog open={showRedriveAllDialog} onOpenChange={setShowRedriveAllDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Redrive All Messages</DialogTitle>
            <DialogDescription>
              Are you sure you want to redrive all messages from {displayName} back to the source
              queue ({queue.source_queue_name})? This will replay approximately{' '}
              {queue.message_count} messages.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRedriveAllDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleRedriveAll} disabled={redriveMessages.isPending}>
              {redriveMessages.isPending ? 'Redriving...' : 'Redrive All Messages'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export function AdminDLQPage() {
  const { data: queuesData, isLoading } = useDLQQueues()
  const [selectedQueue, setSelectedQueue] = useState<DLQQueueInfo | null>(null)

  if (selectedQueue) {
    return (
      <div className="p-8">
        <QueueDetail queue={selectedQueue} onBack={() => setSelectedQueue(null)} />
      </div>
    )
  }

  return (
    <div className="p-8 space-y-8">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Dead Letter Queues</h1>
        <p className="text-muted-foreground">Monitor and manage failed message processing</p>
      </div>

      {/* Summary */}
      {queuesData && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Failed Messages</CardTitle>
            {queuesData.total_messages > 0 && <AlertTriangle className="h-4 w-4 text-amber-500" />}
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{queuesData.total_messages}</div>
            <p className="text-xs text-muted-foreground">
              Across {queuesData.queues.length} queues
            </p>
          </CardContent>
        </Card>
      )}

      {/* Queue List */}
      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-32 bg-gray-200 rounded-lg animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {queuesData?.queues.map((queue) => (
            <QueueCard key={queue.name} queue={queue} onSelect={setSelectedQueue} />
          ))}
        </div>
      )}

      {/* No queues state */}
      {!isLoading && queuesData?.queues.length === 0 && (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            No DLQ queues configured or accessible
          </CardContent>
        </Card>
      )}
    </div>
  )
}
